import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSearch } from '@tanstack/react-router';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { recaptchaConfig } from '@api/auth/config';
import { getQuickStartCookie, setQuickStartCookie } from '@utils/quickStartCookie';
import { usePostStorefrontPublicApiV1SignupQuickStart } from '@api/__generated__/accounts_public/signup/signup';
import { parseValidationError } from '@api/errors';
import { useSignupSuccess } from '@hooks/useSignupSuccess';
import { QuickStartSpinner } from './QuickStartSpinner';

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    top: '30vh',
    left: '40%',
    width: '20%',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      top: '30vh',
      left: '25%',
      width: '50%',
    },
  },
}));

export default function QuickStart() {
  const { classes } = useStyles();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { utm_source: utmSource, utm_medium: utmMedium, utm_term: utmTerm, utm_campaign: utmCampaign } = useSearch({ from: '/signup' });
  const { authenticate, isAuthenticating } = useSignupSuccess();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const { mutateAsync: quickStart, isPending } = usePostStorefrontPublicApiV1SignupQuickStart();
  const handleQuickStart = useCallback(async () => {
    setError(null);

    // If a store was already created (beforeLoad login failed), retry login rather than creating a new store.
    const existingCookie = await getQuickStartCookie();
    if (existingCookie) {
      const errorMessage = await authenticate('Store', 'Builder', existingCookie.email, existingCookie.password);
      if (!errorMessage) {
        window.location.href = `/builder/${existingCookie.lockerId}`;
        return;
      }
    }

    // New user: create a blank store.
    let recaptchaToken: string | null = null;
    if (!recaptchaConfig.bypassRecaptcha) {
      if (!executeRecaptcha) return;
      recaptchaToken = await executeRecaptcha('quick_start');
    }

    try {
      const response = await quickStart({ data: { recaptchaToken } });

      if (!response?.success || !response?.result?.lockerId) {
        setError('Failed to create store. Please try again.');
        return;
      }

      const { lockerId, email, password } = response.result;

      await setQuickStartCookie({ lockerId, email, password, utmSource, utmMedium, utmTerm, utmCampaign });

      const errorMessage = await authenticate('Store', 'Builder', email, password);
      if (!errorMessage) window.location.href = `/builder/${lockerId}`;
    } catch (err) {
      setError(parseValidationError(err));
      started.current = false;
    }
  }, [executeRecaptcha, quickStart, utmSource, utmMedium, utmTerm, utmCampaign, authenticate]);

  useEffect(() => {
    if (started.current) return;
    if (!recaptchaConfig.bypassRecaptcha && !executeRecaptcha) return;
    started.current = true;
    handleQuickStart();
  }, [executeRecaptcha, handleQuickStart]);

  if (error) {
    return (
      <Box className={classes.container} sx={{ width: '100%' }}>
        <Alert severity='error'>{error}</Alert>
        <Button variant='contained' onClick={handleQuickStart} disabled={isPending || isAuthenticating}>
          Try Again
        </Button>
      </Box>
    );
  }

  return <QuickStartSpinner building={isPending || isAuthenticating} />;
}
